import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';

const API_BASE_URL = '/api/v1';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json' },
});

/** Attach JWT token to every outgoing request */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = localStorage.getItem('luna-portal-token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

/** Handle 401 responses — redirect to login */
apiClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      localStorage.removeItem('luna-portal-token');
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

// ─── Types ──────────────────────────────────────────────────
export interface PaginatedResponse<T> {
  data: T[];
  meta: { total: number; page: number; pageSize: number; totalPages: number };
}

export interface SingleResponse<T> {
  data: T;
}

export interface Submission {
  id: string;
  supplierId: string;
  productNameEn: string;
  productNameZh: string | null;
  /** productLine is stored inside specsJson.productLine */
  modelNumber: string | null;
  descriptionEn: string | null;
  specsJson: Record<string, unknown> | null;
  status: string;
  reviewNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── API Functions ──────────────────────────────────────────
export const submissionApi = {
  list: (params?: Record<string, unknown>) =>
    apiClient.get<PaginatedResponse<Submission>>('/m2/submissions/mine', { params }),

  getById: (id: string) =>
    apiClient.get<SingleResponse<Submission>>(`/m2/submissions/${id}`),

  create: (data: {
    supplierName?: string;
    contactEmail?: string;
    productNameEn: string;
    productNameZh?: string;
    productLine?: string;
    modelNumber?: string;
    description?: string;
    specsJson?: Record<string, unknown>;
  }) =>
    apiClient.post<SingleResponse<Submission>>('/m2/submissions', data),

  update: (id: string, data: {
    productNameEn?: string;
    productNameZh?: string;
    productLine?: string;
    modelNumber?: string;
    description?: string;
    specsJson?: Record<string, unknown>;
  }) =>
    apiClient.patch<SingleResponse<Submission>>(`/m2/submissions/${id}`, data),

  /** Transition from DRAFT to SUBMITTED */
  submit: (id: string) =>
    apiClient.post(`/m2/submissions/${id}/submit`),

  /** Upload documents for a submission */
  uploadDocuments: (id: string, formData: FormData) =>
    apiClient.post(`/m2/submissions/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300_000, // 5 min for large uploads
    }),

  /** AI translate submission content (EN + ZH) — requires saved submission id */
  aiTranslate: (id: string) =>
    apiClient.post<SingleResponse<Submission>>(`/m2/submissions/${id}/ai-translate`),
};

export interface SupplierUpdateRequest {
  id: string;
  supplierId: string;
  supplierName: string | null;
  productId: string | null;
  productCode: string | null;
  requestType: 'PRICE_UPDATE' | 'SPEC_UPDATE' | 'AVAILABILITY_UPDATE' | 'NEW_PRODUCT';
  updateData: Record<string, unknown>;
  notes: string | null;
  attachmentUrls: string[] | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'APPLIED';
  reviewNotes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export const supplierUpdateApi = {
  submit: (data: {
    supplierId: string;
    supplierName?: string;
    productId?: string;
    productCode?: string;
    requestType: string;
    updateData: Record<string, unknown>;
    notes?: string;
  }) =>
    apiClient.post<{ success: boolean; data: SupplierUpdateRequest }>('/m2/supplier-updates', data),

  listBySupplierId: (supplierId: string, page = 1, pageSize = 20) =>
    apiClient.get<{ success: boolean; data: SupplierUpdateRequest[]; total: number; page: number; pageSize: number }>(
      `/m2/supplier-updates/by-supplier/${supplierId}`,
      { params: { page, pageSize } },
    ),
};

export { apiClient };
